import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { useCustomerLocaleTag } from '../../shared/hooks/useCustomerLocaleTag.js';
import {
  PTR_HORIZONTAL_IGNORE_ATTR,
  PTR_HORIZONTAL_IGNORE_VALUE,
} from '../../shared/hooks/usePullToRefresh.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { fetchPickupSlots } from '../checkout/pickupSlotsApi.js';
import {
  formatPickupDayChip,
  formatPickupSlotDateTime,
  formatPickupSlotTime,
  getLocalDayKey,
  groupPickupSlotsByDay,
  resolveInitialPickupDayKey,
} from './pickupSlotPresentation.js';

export interface PickupSlotSelection {
  startAt: string;
  endAt: string;
}

interface PickupSlotPickerProps {
  kioskId: number;
  pickupPointId: number;
  value: PickupSlotSelection | null;
  onChange: (slot: PickupSlotSelection | null) => void;
}

const SLOT_CHIP_BASE =
  'flex h-11 w-full min-w-0 items-center justify-center rounded-xl border px-2 py-2 text-sm font-medium transition-[background-color,border-color,color,box-shadow] focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-[var(--color-accent)]';

const SLOT_CHIP_IDLE =
  'cursor-pointer border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-on-surface)] hover:border-[var(--color-accent)]/40 hover:bg-[var(--color-surface-elevated)]';

const SLOT_CHIP_SELECTED =
  'cursor-pointer border-[var(--color-accent)] bg-[var(--color-accent)] text-[var(--color-accent-foreground)] shadow-[var(--shadow-card)]';

const SLOT_CHIP_UNAVAILABLE =
  'cursor-not-allowed border-[var(--color-border)] bg-[var(--color-surface-muted)] text-[var(--color-on-surface-disabled)] opacity-70';

const DAY_CHIP_BASE =
  'flex min-h-11 min-w-[5.25rem] shrink-0 snap-start cursor-pointer flex-col items-center justify-center rounded-xl border-2 px-3 py-2 text-center transition-[background-color,border-color,color,box-shadow] focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-[var(--color-accent)]';

const DAY_CHIP_IDLE =
  'border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-on-surface)] hover:border-[var(--color-accent)]/40 hover:bg-[var(--color-surface-elevated)]';

const DAY_CHIP_SELECTED =
  'border-[var(--color-accent)] bg-[var(--color-accent)]/12 text-[var(--color-on-surface)] shadow-[var(--shadow-card)]';

function PickupSlotPickerSkeleton(): JSX.Element {
  return (
    <div
      className="flex w-full min-w-0 flex-col gap-4"
      data-testid="pickup-slot-picker-loading"
      aria-busy="true"
    >
      <div className="flex w-full min-w-0 gap-2 overflow-hidden">
        {['sk-day-1', 'sk-day-2', 'sk-day-3', 'sk-day-4'].map((key) => (
          <div
            key={key}
            className="h-16 min-w-[5.25rem] shrink-0 animate-pulse rounded-xl bg-[var(--color-surface-muted)]"
          />
        ))}
      </div>
      <div className="grid w-full min-w-0 grid-cols-3 gap-2 sm:grid-cols-4">
        {['sk-time-1', 'sk-time-2', 'sk-time-3', 'sk-time-4', 'sk-time-5', 'sk-time-6'].map(
          (key) => (
            <div
              key={key}
              className="h-11 w-full animate-pulse rounded-xl bg-[var(--color-surface-muted)]"
            />
          ),
        )}
      </div>
    </div>
  );
}

export function PickupSlotPicker({
  kioskId,
  pickupPointId,
  value,
  onChange,
}: PickupSlotPickerProps): JSX.Element {
  const { t } = useTranslation('customer');
  const tenantCode = useTenantCode();
  const localeTag = useCustomerLocaleTag();
  const { fromIso, toIso, rangeDay } = useMemo(() => {
    const from = new Date();
    const to = new Date(from.getTime() + 7 * 24 * 60 * 60_000);
    return {
      fromIso: from.toISOString(),
      toIso: to.toISOString(),
      rangeDay: from.toISOString().slice(0, 10),
    };
  }, []);
  const { data, isPending, isError } = useQuery({
    queryKey: customerQueryKeys.pickupSlots(tenantCode, kioskId, pickupPointId, rangeDay),
    queryFn: () =>
      fetchPickupSlots({
        tenantCode,
        kioskId,
        pickupPointId,
        from: fromIso,
        to: toIso,
      }),
    enabled: pickupPointId > 0,
    staleTime: 60_000,
  });

  const allSlots = useMemo(() => data?.slots ?? [], [data?.slots]);
  // Include unavailable slots so API `available:false` can render muted/disabled chips.
  const dayGroups = useMemo(() => groupPickupSlotsByDay(allSlots), [allSlots]);

  const valueDayKey = value !== null ? getLocalDayKey(value.startAt) : null;
  const [focusedDayKey, setFocusedDayKey] = useState<string | null>(null);

  // FIX 4.1: focused browse day wins over the selected slot's day so tapping
  // another day can show that day's times without the value snapping focus back.
  const activeDayKey = useMemo(() => {
    if (focusedDayKey !== null && dayGroups.some((group) => group.dayKey === focusedDayKey)) {
      return focusedDayKey;
    }
    if (valueDayKey !== null && dayGroups.some((group) => group.dayKey === valueDayKey)) {
      return valueDayKey;
    }
    return resolveInitialPickupDayKey(dayGroups, null);
  }, [dayGroups, valueDayKey, focusedDayKey]);

  if (isPending) {
    return (
      <div className="flex w-full min-w-0 flex-col gap-2">
        <p className="text-sm text-[var(--color-on-surface-muted)]">
          {t('checkout.scheduledPickupLoading')}
        </p>
        <PickupSlotPickerSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <p
        className="w-full rounded-lg border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 px-3 py-3 text-sm text-[var(--color-danger)]"
        role="alert"
      >
        {t('checkout.scheduledPickupError')}
      </p>
    );
  }

  if (allSlots.length === 0) {
    return (
      <p className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-elevated)] px-3 py-3 text-sm text-[var(--color-on-surface-muted)]">
        {t('checkout.scheduledPickupEmpty')}
      </p>
    );
  }

  const activeDayGroup =
    dayGroups.find((group) => group.dayKey === activeDayKey) ?? dayGroups[0] ?? null;
  const dayChipLabels = {
    today: t('checkout.scheduledPickupToday'),
    tomorrow: t('checkout.scheduledPickupTomorrow'),
  };

  return (
    <fieldset
      className="m-0 flex w-full min-w-0 flex-col gap-4 border-0 p-0"
      data-testid="pickup-slot-picker"
    >
      <legend className="sr-only">{t('checkout.scheduledPickupLegend')}</legend>

      {value !== null ? (
        <div
          className="w-full rounded-lg border border-[var(--color-accent)]/35 bg-[var(--color-accent)]/10 px-3 py-3"
          role="status"
          data-testid="pickup-slot-selected-summary"
        >
          <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-on-surface-muted)]">
            {t('checkout.scheduledPickupSelectedLabel')}
          </p>
          <p className="mt-1 text-base font-semibold text-[var(--color-on-surface)]">
            {formatPickupSlotDateTime(value.startAt, localeTag)}
          </p>
        </div>
      ) : null}

      <div className="flex w-full min-w-0 flex-col gap-2">
        <p
          id="pickup-slot-day-label"
          className="text-sm font-medium text-[var(--color-on-surface)]"
        >
          {t('checkout.scheduledPickupDateLegend')}
        </p>
        <div
          className="w-full min-w-0 touch-pan-x touch-pan-y overflow-x-auto overscroll-x-contain pb-1"
          role="radiogroup"
          aria-labelledby="pickup-slot-day-label"
          {...{ [PTR_HORIZONTAL_IGNORE_ATTR]: PTR_HORIZONTAL_IGNORE_VALUE }}
          data-testid="pickup-slot-day-strip"
        >
          <div className="inline-flex min-w-full gap-2 snap-x snap-mandatory">
          {dayGroups.map((group) => {
            const chip = formatPickupDayChip(group.dayKey, localeTag, dayChipLabels);
            const selected = activeDayGroup?.dayKey === group.dayKey;
            const inputId = `pickup-slot-day-${group.dayKey}`;

            return (
              <label
                key={group.dayKey}
                htmlFor={inputId}
                className={`${DAY_CHIP_BASE} ${selected ? DAY_CHIP_SELECTED : DAY_CHIP_IDLE}`}
              >
                <input
                  id={inputId}
                  type="radio"
                  name="pickup-slot-day"
                  className="sr-only"
                  checked={selected}
                  onChange={() => {
                    const dayKey = group.dayKey;
                    setFocusedDayKey(dayKey);
                    if (value !== null && getLocalDayKey(value.startAt) !== dayKey) {
                      onChange(null);
                    }
                  }}
                />
                <span className="text-sm font-semibold leading-tight">{chip.primary}</span>
                <span className="mt-0.5 text-xs leading-tight text-[var(--color-on-surface-muted)]">
                  {chip.secondary}
                </span>
              </label>
            );
          })}
          </div>
        </div>
      </div>

      {activeDayGroup !== null ? (
        <div className="flex w-full min-w-0 flex-col gap-2">
          <p
            id="pickup-slot-time-label"
            className="text-sm font-medium text-[var(--color-on-surface)]"
          >
            {t('checkout.scheduledPickupTimeLegend')}
          </p>
          <div
            className="grid w-full min-w-0 grid-cols-3 gap-2 sm:grid-cols-4"
            role="radiogroup"
            aria-labelledby="pickup-slot-time-label"
          >
            {activeDayGroup.slots.map((slot) => {
              const selected = value?.startAt === slot.startAt;
              const inputId = `pickup-slot-${slot.startAt}`;
              const unavailable = slot.available === false;
              let chipClass = SLOT_CHIP_IDLE;
              if (unavailable) {
                chipClass = SLOT_CHIP_UNAVAILABLE;
              } else if (selected) {
                chipClass = SLOT_CHIP_SELECTED;
              }

              // Inline CSS vars so jsdom getComputedStyle can resolve selected chip
              // contrast (Tailwind arbitrary classes alone do not paint in Jest).
              const selectedChipStyle =
                selected && !unavailable
                  ? {
                      backgroundColor: 'var(--color-accent)',
                      color: 'var(--color-accent-foreground)',
                    }
                  : undefined;

              return (
                <label
                  key={slot.startAt}
                  htmlFor={inputId}
                  className={`${SLOT_CHIP_BASE} ${chipClass}`}
                  style={selectedChipStyle}
                  data-unavailable={unavailable ? 'true' : undefined}
                  data-selected={selected && !unavailable ? 'true' : undefined}
                >
                  <input
                    id={inputId}
                    type="radio"
                    name="pickup-slot"
                    className="sr-only"
                    checked={selected}
                    disabled={unavailable}
                    onChange={() => {
                      if (unavailable) {
                        return;
                      }
                      onChange({ startAt: slot.startAt, endAt: slot.endAt });
                    }}
                  />
                  {formatPickupSlotTime(slot.startAt, localeTag)}
                </label>
              );
            })}
          </div>
        </div>
      ) : null}
    </fieldset>
  );
}
